import React, { Suspense, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation, useParams } from 'react-router-dom';
import {AppBar, Toolbar, Typography, Button, Container, Box, Stack, useTheme, useMediaQuery, CircularProgress, Badge, Avatar, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Divider} from '@mui/material';
import {
  Explore as ExploreIcon,
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  VideoLibrary as VideoLibraryIcon,
  Assignment as AssignmentIcon,
  Message as MessageIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { getAvatarGradient, getInitials } from './utils/avatarUtils';
import MobileNavigation from './components/navigation/MobileNavigation';
import Logo from './components/Logo';
import { useAuth0User } from './components/context/Auth0UserContext';
import { useUser } from './components/context/UserContext';
import { AuthModalProvider, useAuthModal } from './components/context/AuthModalContext';
import { MessagingProvider, useMessagingNotifications } from './context/MessagingContext';
import { RequestsNotificationProvider, useRequestsNotifications } from './context/RequestsNotificationContext';
import { FeatureFlagsProvider } from './context/FeatureFlagsContext';
import GlobalAuthModal from './components/auth/GlobalAuthModal';
import './App.css';
import ProtectedRoute from "./components/auth/ProtectedRoute";
import AdminProtectedRoute from "./components/auth/AdminProtectedRoute";
import PublicAccessGuard from './components/auth/PublicAccessGuard';
import HomePage from "./components/home/HomePage";
import Footer from './components/common/Footer';
import NotFoundPage from './components/common/NotFoundPage';
import BotImpersonationBanner from './components/common/BotImpersonationBanner';
import { getCurrentUser } from './services/api';

// Build version for deploy verification
const BUILD_VERSION = process.env.REACT_APP_BUILD_VERSION || 'development';
(window as any).__BUILD_VERSION__ = BUILD_VERSION;

// Lazy load heavy components for better performance
const GuestProfilePage = React.lazy(() => import('./components/guests/GuestProfilePage'));
const HostProfilePage = React.lazy(() => import('./components/host/HostProfilePage'));
const Dashboard = React.lazy(() => import('./components/dashboard/Dashboard'));
const GuestShootCreation = React.lazy(() => import('./components/guestshoot/GuestShootCreation'));
const GuestShootDetails = React.lazy(() => import('./components/guestshoot/GuestShootDetails'));
const EditGuestShoot = React.lazy(() => import('./components/guestshoot/EditGuestShoot'));
const MyShootsPage = React.lazy(() => import('./components/guestshoot/MyShootsPage'));
const RequestsPage = React.lazy(() => import('./components/requests/RequestsPage'));
const ConfirmBookingPage = React.lazy(() => import('./components/requests/ConfirmBookingPage'));
const SuggestedInvitesPage = React.lazy(() => import('./components/suggestions/SuggestedInvitesPage'));
const EditProfilePage = React.lazy(() => import('./components/settings/EditProfilePage'));
const SettingsPage = React.lazy(() => import('./components/settings/SettingsPage'));
const ExploreGuestSpots = React.lazy(() => import('./components/explore/ExploreGuestSpots'));
const RequestGuestSpot = React.lazy(() => import('./components/guestspot/RequestGuestSpot'));
const EditGuestSpot = React.lazy(() => import('./components/guestspot/EditGuestSpot'));
const MyRequestsPage = React.lazy(() => import('./components/requests/MyRequestsPage'));
const InviteSignUp = React.lazy(() => import('./components/auth/InviteSignUp'));
const Auth0Callback = React.lazy(() => import('./components/auth/Auth0Callback'));
const LogIn = React.lazy(() => import('./components/auth/LogIn'));
const ContinueRequestPage = React.lazy(() => import("./components/requests/ContinueRequestPage"));
const PublicShootPage = React.lazy(() => import("./components/home/PublicShootPage"));
const GuestSpotDetails = React.lazy(() => import('./components/guestspot/GuestSpotDetails'));
const ProfileRedirect = React.lazy(() => import('./components/profile/ProfileRedirect'));
const AdminLayout = React.lazy(() => import('./components/admin/AdminLayout'));
const AdminDashboard = React.lazy(() => import('./components/admin/AdminDashboard'));
const UsersManagement = React.lazy(() => import('./components/admin/UsersManagement'));
const GuestSpotsManagement = React.lazy(() => import('./components/admin/GuestSpotsManagement'));
const ShootsManagement = React.lazy(() => import('./components/admin/ShootsManagement'));
const RequestsManagement = React.lazy(() => import('./components/admin/RequestsManagement'));
const Auth0Management = React.lazy(() => import('./components/admin/Auth0Management'));
const AdminSettingsPage = React.lazy(() => import('./components/admin/AdminSettingsPage'));
const LogViewer = React.lazy(() => import('./components/admin/LogViewer'));
const RequestTracer = React.lazy(() => import('./components/admin/RequestTracer'));
const HealthMonitor = React.lazy(() => import('./components/admin/HealthMonitor'));
const ErrorDashboard = React.lazy(() => import('./components/admin/ErrorDashboard'));
const FeatureFlagsManagement = React.lazy(() => import('./components/admin/FeatureFlagsManagement'));
const AdminAnalyticsDashboard = React.lazy(() => import('./components/admin/AdminAnalyticsDashboard'));
const InvestorMetricsPage = React.lazy(() => import('./components/admin/InvestorMetricsPage'));
const EmailMonitoringDashboard = React.lazy(() => import('./components/admin/EmailMonitoringDashboard'));
const BotsPage = React.lazy(() => import('./components/admin/BotsPage'));
const BotDetailPage = React.lazy(() => import('./components/admin/BotDetailPage'));
const BotMonitorPage = React.lazy(() => import('./components/admin/BotMonitorPage'));
const BotInsightsPage = React.lazy(() => import('./components/admin/BotInsightsPage'));
const SimpleMessagingPage = React.lazy(() => import('./components/messaging/SimpleMessagingPage'));
const GuestOnboarding = React.lazy(() => import('./components/onboarding/GuestOnboarding'));
const GrowthLedgerPage = React.lazy(() => import('./components/growth/GrowthLedger'));
const PublicPortfolioPage = React.lazy(() => import('./components/growth/PublicPortfolio'));
const OutcomesFeedPage = React.lazy(() => import('./components/growth/OutcomesFeed'));
const TermsOfService = React.lazy(() => import('./components/legal/TermsOfService'));
const PrivacyPolicy = React.lazy(() => import('./components/legal/PrivacyPolicy'));
const TeamPage = React.lazy(() => import('./components/about/TeamPage'));
const TestLoginBackdoor = process.env.REACT_APP_ENABLE_TEST_LOGIN === 'true'
  ? React.lazy(() => import('./components/auth/TestLoginBackdoor'))
  : (() => null) as React.FC;

// Loading component for Suspense fallback
const PageLoader: React.FC = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '60vh',
      flexDirection: 'column',
      gap: 2
    }}
  >
    <CircularProgress size={40} sx={{ color: 'primary.main' }} />
    <Typography variant="body2" color="text.secondary">
      Loading...
    </Typography>
  </Box>
);

const LegacyGuestSpotRequestsRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const canonicalParams = new URLSearchParams({ tab: 'received' });

  if (id && /^\d+$/.test(id)) {
    canonicalParams.set('spot', id);
  }

  const legacyHighlight = new URLSearchParams(location.search).get('highlight');
  if (legacyHighlight && /^\d+$/.test(legacyHighlight)) {
    canonicalParams.set('highlight', legacyHighlight);
  }

  return <Navigate to={`/requests?${canonicalParams.toString()}`} replace />;
};

// Ensure the window scrolls to top on every route change
function ScrollToTop() {
  const { pathname } = useLocation();

  React.useEffect(() => {
    // Scroll window and any scrollable root container to top
    try {
      window.scrollTo(0, 0);
      const root = document.getElementById('root');
      if (root) root.scrollTop = 0;
      const scrollingElement = document.scrollingElement || document.documentElement;
      if (scrollingElement) scrollingElement.scrollTop = 0;
    } catch {
      // no-op if environment doesn't support scrolling
    }
  }, [pathname]);

  return null;
}

// Simple switch between Auth0 and original authentication
const useAuth0 = process.env.REACT_APP_USE_AUTH0 === 'true';

// Auth0 App Content - Uses Auth0 authentication
function Auth0AppContent() {
  const { user, logout } = useAuth0User();
  return (
    <AuthModalProvider>
      <Auth0AppContentWithModal user={user} logout={logout} />
    </AuthModalProvider>
  );
}

function Auth0AppContentWithModal({ user, logout }: { user: any, logout: () => void }) {
  const { openModal } = useAuthModal();
  return (
    <>
      <AppContentComponent user={user} logout={logout} openAuthModal={openModal} />
      <GlobalAuthModal />
    </>
  );
}

// Original App Content - Uses original authentication
function OriginalAppContent() {
  const { user, logout } = useUser();
  return <AppContentComponent user={user} logout={logout} />;
}

// Main App Content - Renders appropriate authentication wrapper
export function AppContent() {
  return useAuth0 ? <Auth0AppContent /> : <OriginalAppContent />;
}

// Shared App Content Component - Contains all app logic
function AppContentComponent({ user, logout, openAuthModal }: { user: any, logout: () => void, openAuthModal?: () => void }) {
  return (
    <MessagingProvider userId={user?.id}>
      <RequestsNotificationProvider userId={user?.id} userRole={user?.role}>
        <FeatureFlagsProvider userId={user?.id}>
          <AppContentInner user={user} logout={logout} openAuthModal={openAuthModal} />
        </FeatureFlagsProvider>
      </RequestsNotificationProvider>
    </MessagingProvider>
  );
}

// Inner component that can use messaging context
function AppContentInner({ user, logout, openAuthModal }: { user: any, logout: () => void, openAuthModal?: () => void }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const { unreadCount } = useMessagingNotifications();
  const { pendingCount, unseenStatusCount } = useRequestsNotifications();
  const hideNavRoutes = new Set(['/auth0-callback']);
  const isAdminRoute = location.pathname === '/admin' || location.pathname.startsWith('/admin/');
  const hideNav = hideNavRoutes.has(location.pathname) || isAdminRoute;
  const isBotImpersonating = (() => {
    if (typeof sessionStorage === 'undefined' || sessionStorage.getItem('botLoginActive') !== 'true') {
      return false;
    }
    try {
      const currentUser = getCurrentUser() || {};
      return Boolean(currentUser.is_bot || currentUser.bot_persona);
    } catch {
      return false;
    }
  })();
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(null);

  // Simple logout handler - logout() function handles redirect
  const handleLogout = () => {
    logout();
    // Note: logout() function handles redirect (Auth0 or original mode)
  };

  const handleLoginClick = (e: React.MouseEvent) => {
    if (useAuth0 && openAuthModal) {
      e.preventDefault();
      openAuthModal();
    }
  };

  return (
    <>
      <ScrollToTop />
      <BotImpersonationBanner />
      <div className="app">
        {!hideNav && (
        <AppBar position="fixed" sx={{ backgroundColor: 'primary.main', top: isBotImpersonating ? 40 : 0 }}>
          <Container maxWidth="lg">
            <Toolbar sx={{ 
              padding: { xs: '0 8px', sm: '0 16px', md: '0 !important' },
              minHeight: { xs: 56, sm: 64 }
            }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                  flexGrow: 1,
                  '& svg': {
                    filter: 'drop-shadow(0 0 8px rgba(255,255,255,0.3))',
                  }
                }}
              >
                <Logo sx={{
                  fontSize: { xs: 28, sm: 32, md: 36 },
                  color: '#fff',
                  transform: 'translateY(-1px)'
                }} />
                <Typography
                  variant="h6"
                  component={Link}
                  to="/"
                  sx={{
                    fontWeight: 600,
                    letterSpacing: '0.5px',
                    color: '#fff',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    fontSize: { xs: '1.1rem', sm: '1.25rem' },
                    display: { xs: isMobile ? 'block' : 'none', md: 'block' }
                  }}
                >
                  Guest Stream
                </Typography>
              </Stack>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  {isMobile ? (
                    // Mobile Navigation
                    <MobileNavigation 
                      user={user} 
                      onLogout={handleLogout}
                      onLoginClick={openAuthModal}
                    />
                  ) : (
                    // Desktop Navigation
                    <>
                      {user ? (
                        // Logged in user navigation - desktop
                        <>
                          {user.role === 'admin' ? (
                            // Admin user - only show Admin and Logout
                            <>
                              <Button color="inherit" component={Link} to="/admin">
                                Admin
                              </Button>
                              <Button color="inherit" onClick={handleLogout}>
                                Logout
                              </Button>
                            </>
                          ) : (
                            // Regular user - consolidated navigation
                            <>
                              <Button color="inherit" component={Link} to="/explore" startIcon={<ExploreIcon sx={{ fontSize: 18 }} />}>
                                Explore
                              </Button>
                              <Button color="inherit" component={Link} to="/dashboard" startIcon={<DashboardIcon sx={{ fontSize: 18 }} />}>
                                Dashboard
                              </Button>
                              <Badge
                                badgeContent={(pendingCount + unseenStatusCount) + unreadCount}
                                color="error"
                                sx={{
                                  '& .MuiBadge-badge': {
                                    fontSize: '0.7rem',
                                    minWidth: '18px',
                                    height: '18px',
                                  }
                                }}
                              >
                                <IconButton
                                  aria-label="Open account menu"
                                  aria-controls={userMenuAnchorEl ? 'account-menu' : undefined}
                                  aria-expanded={userMenuAnchorEl ? 'true' : undefined}
                                  aria-haspopup="menu"
                                  onClick={(e) => setUserMenuAnchorEl(e.currentTarget)}
                                  sx={{ p: 0 }}
                                >
                                  <Avatar
                                    src={user.profile_image || undefined}
                                    alt={user.display_name || user.username || 'Account'}
                                    sx={{
                                      width: 34,
                                      height: 34,
                                      background: user.profile_image ? undefined : getAvatarGradient(user.display_name || user.username || '').gradient,
                                      fontSize: '0.85rem',
                                      fontWeight: 700,
                                      border: '2px solid rgba(255,255,255,0.4)',
                                      transition: 'border-color 0.2s',
                                      '&:hover': {
                                        borderColor: 'white',
                                      }
                                    }}
                                  >
                                    {getInitials(user.display_name || user.username || '')}
                                  </Avatar>
                                </IconButton>
                              </Badge>
                              <Menu
                                id="account-menu"
                                anchorEl={userMenuAnchorEl}
                                open={Boolean(userMenuAnchorEl)}
                                onClose={() => setUserMenuAnchorEl(null)}
                                PaperProps={{
                                  sx: {
                                    minWidth: 200,
                                    borderRadius: 2,
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                                    mt: 1,
                                  }
                                }}
                                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                              >
                                <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    {user.display_name || user.username}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {user.email || ''}
                                  </Typography>
                                </Box>
                                <MenuItem component={Link} to="/profile" onClick={() => setUserMenuAnchorEl(null)}>
                                  <ListItemIcon><PersonIcon fontSize="small" sx={{ color: '#2D2070' }} /></ListItemIcon>
                                  <ListItemText>Profile</ListItemText>
                                </MenuItem>
                                <MenuItem component={Link} to="/my-shoots" onClick={() => setUserMenuAnchorEl(null)}>
                                  <ListItemIcon><VideoLibraryIcon fontSize="small" sx={{ color: '#2D2070' }} /></ListItemIcon>
                                  <ListItemText>My Shoots</ListItemText>
                                </MenuItem>
                                <MenuItem component={Link} to="/requests" onClick={() => setUserMenuAnchorEl(null)}>
                                  <ListItemIcon>
                                    <Badge badgeContent={pendingCount + unseenStatusCount} color="error" sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', minWidth: 16, height: 16 } }}>
                                      <AssignmentIcon fontSize="small" sx={{ color: '#2D2070' }} />
                                    </Badge>
                                  </ListItemIcon>
                                  <ListItemText>Requests</ListItemText>
                                </MenuItem>
                                <MenuItem component={Link} to="/messages" onClick={() => setUserMenuAnchorEl(null)}>
                                  <ListItemIcon>
                                    <Badge badgeContent={unreadCount} color="error" sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', minWidth: 16, height: 16 } }}>
                                      <MessageIcon fontSize="small" sx={{ color: '#2D2070' }} />
                                    </Badge>
                                  </ListItemIcon>
                                  <ListItemText>Messages</ListItemText>
                                </MenuItem>
                                <MenuItem component={Link} to="/settings" onClick={() => setUserMenuAnchorEl(null)}>
                                  <ListItemIcon><SettingsIcon fontSize="small" sx={{ color: '#2D2070' }} /></ListItemIcon>
                                  <ListItemText>Settings</ListItemText>
                                </MenuItem>
                                <Divider />
                                <MenuItem onClick={() => { setUserMenuAnchorEl(null); handleLogout(); }}>
                                  <ListItemIcon><LogoutIcon fontSize="small" sx={{ color: '#f44336' }} /></ListItemIcon>
                                  <ListItemText sx={{ color: '#f44336' }}>Logout</ListItemText>
                                </MenuItem>
                              </Menu>
                            </>
                          )}
                        </>
                      ) : (
                        // Not logged in navigation - desktop
                        <>
                          <Button color="inherit" component={Link} to="/explore">
                            Explore
                          </Button>
                          <Button
                            color="inherit"
                            {...(useAuth0 ? {} : { component: Link, to: "/login" })}
                            onClick={handleLoginClick}
                          >
                            Sign In
                          </Button>
                        </>
                      )}
                    </>
                  )}
                </Box>
            </Toolbar>
          </Container>
        </AppBar>
        )}

        <Box component="main" sx={{ pt: hideNav ? (isBotImpersonating ? '40px' : 0) : (isBotImpersonating ? '104px' : '64px') }}>
            <PublicAccessGuard>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                {/* Public Routes */}
                <Route path="/" element={<HomePage />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/team" element={<TeamPage />} />
                {process.env.REACT_APP_ENABLE_TEST_LOGIN === 'true' && (
                  <Route path="/__test-login" element={<TestLoginBackdoor />} />
                )}
                
                {/* Conditional Authentication Routes - Simple Switch */}
                {useAuth0 ? (
                  // Auth0 Social Login Routes (modal-based, no /login route needed)
                  <>
                    <Route path="/auth0-callback" element={<Auth0Callback />} />
                    {/* Redirect deprecated /login and /signup to home in Auth0 mode */}
                    <Route path="/login" element={<Navigate to="/" replace />} />
                    <Route path="/signup" element={<Navigate to="/" replace />} />
                  </>
                ) : (
                  // Original Username/Password Login Routes
                  <>
                    <Route path="/login" element={<LogIn />} />
                    <Route path="/signup" element={<InviteSignUp requireInviteCode={false} showSocialFields={true} signupEnabled={true} />} />
                  </>
                )}
                
                {/* Invite signup - works with both auth systems */}
                <Route path="/invite-signup" element={<InviteSignUp requireInviteCode={true} showSocialFields={true} signupEnabled={true} />} />
                <Route path="/explore" element={<ExploreGuestSpots />} />
                <Route path="/continue-request" element={<ContinueRequestPage />} />

                {/* Public routes with shoot/spot viewing */}
                <Route path="/creator/:username/:shootSlug/:spotSlug/request" element={<RequestGuestSpot />} />
                <Route path="/creator/:username/:shootSlug/:spotSlug" element={<GuestSpotDetails />} />
                <Route path="/shoots/:id" element={<GuestShootDetails />} />
                {/* Deprecated: <Route path="/guest-shoot/:id" element={<GuestShootDetails />} /> */}
                <Route path="/guest-spot/:id" element={<GuestSpotDetails />} />
                <Route path="/request/:requestId/confirm" element={<ConfirmBookingPage />} />

                {/* Protected Routes - All logged-in users can access these features */}
                <Route element={<ProtectedRoute />}>
                  <Route path="/onboarding" element={<GuestOnboarding />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/my-shoots" element={<MyShootsPage />} />
                  <Route path="/requests" element={<RequestsPage />} />
                  <Route path="/messages" element={<SimpleMessagingPage />} />
                  <Route path="/suggested-invites" element={<SuggestedInvitesPage />} />
                  <Route path="/create-guest-shoot" element={<GuestShootCreation />} />
                  <Route path="/my-requests" element={<MyRequestsPage />} />

                  {/* Routes that require ownership of the resource */}
                  <Route path="/edit-guest-shoot/:slug" element={<EditGuestShoot />} />
                  <Route path="/create-guest-spot/:shootId" element={<EditGuestSpot />} />
                  <Route path="/edit-guest-spot/:id" element={<EditGuestSpot />} />
                  {/* <Route path="/delete-guest-shoot/:id" element={<DeleteGuestShoot />} /> */}
                  {/* <Route path="/delete-guest-spot/:id" element={<DeleteGuestSpot />} /> */}
                  <Route path="/guest-spot/:id/requests" element={<LegacyGuestSpotRequestsRedirect />} />
                  <Route path="/shoots/:id/guests" element={<RequestsPage />} />
                </Route>

                {/* Shared Protected Routes (either role, but must be logged in) */}
                <Route element={<ProtectedRoute />}>
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/settings/profile" element={<Navigate to="/settings/edit-profile" replace />} />
                  <Route path="/settings/edit-profile" element={<EditProfilePage />} />
                  <Route path="/growth" element={<GrowthLedgerPage />} />
                </Route>

                {/* Public growth routes */}
                <Route path="/portfolio/:username" element={<PublicPortfolioPage />} />
                <Route path="/outcomes" element={<OutcomesFeedPage />} />

                {/* Profile routes - must come before generic catch-all */}
                <Route path="/profile" element={<ProfileRedirect />} />
                <Route path="/guest/:username" element={<GuestProfilePage />} />
                <Route path="/host/:username" element={<HostProfilePage />} />

                {/* Admin Routes - Admin access only */}
                <Route element={<AdminProtectedRoute />}>
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={<AdminDashboard />} />
                    <Route path="dashboard" element={<AdminDashboard />} />
                    <Route path="users" element={<UsersManagement />} />
                    <Route path="guest-spots" element={<GuestSpotsManagement />} />
                    <Route path="shoots" element={<ShootsManagement />} />
                    <Route path="requests" element={<RequestsManagement />} />
                    <Route path="logs" element={<LogViewer />} />
                    <Route path="tracer" element={<RequestTracer />} />
                    <Route path="health" element={<HealthMonitor />} />
                    <Route path="errors" element={<ErrorDashboard />} />
                    <Route path="auth0" element={<Auth0Management />} />
                    <Route path="settings" element={<AdminSettingsPage />} />
                    <Route path="feature-flags" element={<FeatureFlagsManagement userId={user?.id} />} />
                    <Route path="analytics" element={<AdminAnalyticsDashboard />} />
                    <Route path="investor-metrics" element={<InvestorMetricsPage />} />
                    <Route path="email-monitoring" element={<EmailMonitoringDashboard />} />
                    <Route path="bots" element={<BotsPage />} />
                    <Route path="bots/monitor" element={<BotMonitorPage />} />
                    <Route path="bots/insights" element={<BotInsightsPage />} />
                    <Route path="bots/:id" element={<BotDetailPage />} />
                  </Route>
                </Route>

                {/* Generic catch-all routes - these must come LAST */}
                <Route path="/creator/:username/:shootSlug" element={<PublicShootPage />} />
                <Route path="/:username/:shootSlug" element={<PublicShootPage />} />
                
                {/* 404 Not Found - absolute last route */}
                <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
            </PublicAccessGuard>
        </Box>
        {!hideNav && <Footer />}
      </div>
    </>
  );
}

function App() {
  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <AppContent />
    </Router>
  );
}

export default App;
